import invariant from "invariant";
import type { MittwaldApi } from "../../api/Mittwald";
import { mittwaldApi } from "../../api/Mittwald";
import App from "./App";
import AppVersion from "./AppVersion";
import InstalledSystemSoftwareList from "./InstalledSystemSoftwareList";

export type AppInstallationApiData =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_App_AppInstallation;

export type LinkedDatabase =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_App_LinkedDatabase;

export type SystemSoftwareDependency =
  MittwaldApi.Components.Schemas.De_Mittwald_V1_App_SystemSoftwareDependency;

export class AppInstallation {
  public readonly data: AppInstallationApiData;
  public readonly id: string;
  public readonly installedSystemSoftware: InstalledSystemSoftwareList;
  public readonly projectId: string;
  public readonly description: string;
  public readonly customDocRootPath?: string;
  public readonly installationPath: string;

  private constructor(data: AppInstallationApiData) {
    this.data = Object.freeze(data);
    this.id = data.id;
    invariant(data.projectId, "projectId not found");
    this.projectId = data.projectId;
    this.description = data.description;
    this.installedSystemSoftware = InstalledSystemSoftwareList.fromApiData(
      data.systemSoftware ?? [],
      this,
    );
    this.customDocRootPath = data.customDocumentRoot;
    this.installationPath = data.installationPath;
  }

  public static fromApiData(data: AppInstallationApiData): AppInstallation {
    return new AppInstallation(data);
  }

  public static useTryLoadById(id?: string): AppInstallation | undefined {
    const data = mittwaldApi.appGetAppinstallation
      .getResource(id ? { path: { appInstallationId: id } } : null)
      .useWatchData({
        optional: true,
        throwOnError: false,
      });
    return data ? new AppInstallation(data) : undefined;
  }

  public useApp(): App {
    return App.useLoadById(this.data.appId);
  }

  public useVersion(): AppVersion {
    const app = this.useApp();
    const versionId = this.data.appVersion.desired;
    return AppVersion.useLoadById(app, versionId);
  }
}

export default AppInstallation;
